import { memo } from 'react';
import { GMButton, ComponentProps as ButtonProps } from '../Buttons/Button';
import { GMButtonList } from '../Buttons/ButtonList';
import { GMModal, ModalProps, GMModalBody, GMModalFooter, GMModalHeader } from './Modal';

export interface ComponentProps extends Pick<ModalProps, 'position' | 'open' | 'darkBackdrop' | 'children' | 'className' | 'fullScreen'> {
    /**
     * Callback for when the backdrop/close icon is clicked. If undefined,
     * clicking the backdrop will not close the dialog and no close
     * icon will be present
     */
    onClose?: () => void;
    /**
     * Buttons to display at bottom of modal
     */
    buttons?: ButtonProps[];
    /**
     * Title of the modal
     */
    title: string;
    /**
     * Class name to apply to the modal body
     */
    modalBodyClassName?: string;
    /**
     * Whether the close callback should be called when the backdrop is clicked
     */
    noCloseOnBackdropClick?: boolean;
    /**
     * Callback when the expand/shrink icon is clicked. If undefined, no icon will be shown.
     */
    onToggleExpand?: () => void;
    /**
     * Callback for when the print icon is clicked. If undefined, no icon will be
     * shown. If null, a disabled icon will be shown.
     */
    onPrint?: (() => void) | null;
}

/**
 * Generic wrapper for a more customized dialog
 */
export const GMDialog: React.FC<ComponentProps> = memo((props) => {
    return (
        <GMModal
            open={props.open}
            onClose={props.noCloseOnBackdropClick ? undefined : props.onClose}
            fullScreen={props.fullScreen}
            className={props.className}
            position={props.position}
        >
            <GMModalHeader
                onClose={props.onClose}
                expanded={props.onToggleExpand && props.fullScreen}
                onToggleExpand={props.onToggleExpand}
                onPrint={props.onPrint}
            >
                {props.title}
            </GMModalHeader>
            <GMModalBody className={props.modalBodyClassName}>
                {props.children}
            </GMModalBody>
            {props.buttons &&
                <GMModalFooter>
                    <GMButtonList
                        name={`gm-modal-${props.title}`}
                        rightButtons={props.buttons.map(({ children, ...buttonProps }, index: number) => {
                            return (
                                <div key={`gm-modal-btn-${index}`}>
                                    <GMButton {...buttonProps}>
                                        {children}
                                    </GMButton>
                                </div>
                            );
                        })}
                    />
                </GMModalFooter>
            }
        </GMModal>
    );
});